/**
 * Phase 27a — dashboard wrapper around the cross-org `/platform/*` API.
 * Every call requires the caller to hold the `platform_admin` role; the
 * server returns 403 otherwise.
 *
 * Adapter over `@commerce-os/api-client`'s `platformApi` (the shared
 * contract), bound to the dashboard's axios instance — same shape as
 * `kdsApi.ts`. The export shape is unchanged for the hooks above it.
 */
import { platformApi as shared } from '@commerce-os/api-client';
import type {
  CreatePlatformOrgRequest,
  CreatePlatformOrgUserRequest,
  CreateSuiteGrantRequest,
  ListPlatformOrgsQuery,
  UpdatePlatformOrgStatusRequest,
} from '@commerce-os/shared-types';
import { getApiClient } from '../lib/axios';

export const platformApi = {
  getStats: () => shared.getPlatformStats(getApiClient()),

  /** Phase 66.0 — where merchants' card money settles, across orgs. */
  getPaymentRouting: (months?: number) => shared.getPlatformPaymentRouting(getApiClient(), months),

  listOrgs: (query: ListPlatformOrgsQuery = {}) =>
    shared.listPlatformOrgs(getApiClient(), query),

  getOrg: (id: string) => shared.getPlatformOrg(getApiClient(), id),

  createOrg: (body: CreatePlatformOrgRequest) =>
    shared.createPlatformOrg(getApiClient(), body),

  updateOrgStatus: (id: string, body: UpdatePlatformOrgStatusRequest) =>
    shared.updatePlatformOrgStatus(getApiClient(), id, body),

  createOrgUser: (orgId: string, body: CreatePlatformOrgUserRequest) =>
    shared.createPlatformOrgUser(getApiClient(), orgId, body),

  resetUserPassword: (userId: string) =>
    shared.resetPlatformUserPassword(getApiClient(), userId),

  /** Enter a merchant org as CommerceOS Admin (60-minute support session). */
  enterOrg: (orgId: string) => shared.startSupportSession(getApiClient(), orgId),

  /** Leave it — back to the admin's home org (CommerceOS). */
  exitOrg: () => shared.endSupportSession(getApiClient()),

  /** 2026-09-30 — the org's plan beside its suite comps. */
  listSuiteGrants: (orgId: string) => shared.listPlatformSuiteGrants(getApiClient(), orgId),

  /** Comp a suite the plan doesn't include (replaces a live comp for it). */
  createSuiteGrant: (orgId: string, body: CreateSuiteGrantRequest) =>
    shared.createPlatformSuiteGrant(getApiClient(), orgId, body),

  /** End a comp now. */
  endSuiteGrant: (orgId: string, grantId: string) =>
    shared.endPlatformSuiteGrant(getApiClient(), orgId, grantId),
};
